import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, PriorityPill, TypePill } from "@/components/StatusPill";
import { relTime } from "@/lib/format";
import { Plus, Search, Ticket as TicketIcon, Building2, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "resolved", label: "Resolved" },
  { key: "overdue", label: "Overdue" },
];

export default function Tickets() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState(params.get("filter") || "all");
  const [outletId, setOutletId] = useState(params.get("outlet_id") || "");
  const [typeFilter] = useState(params.get("type") || "");
  const [tickets, setTickets] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({}); // { outletId: true } → section collapsed

  useEffect(() => {
    (async () => {
      const query = new URLSearchParams();
      if (filter !== "all") query.set("filter", filter);
      if (outletId) query.set("outlet_id", outletId);
      if (typeFilter) query.set("type", typeFilter);
      const [t, o] = await Promise.all([
        api.get(`/tickets?${query.toString()}`),
        api.get("/outlets"),
      ]);
      setTickets(t.data);
      setOutlets(o.data);
    })();
  }, [filter, outletId, typeFilter]);

  const outletMap = useMemo(() => Object.fromEntries(outlets.map(o => [o.id, o])), [outlets]);

  const list = useMemo(() => {
    if (!tickets) return null;
    if (!q.trim()) return tickets;
    const s = q.toLowerCase();
    return tickets.filter(t =>
      t.title.toLowerCase().includes(s) ||
      t.description.toLowerCase().includes(s) ||
      outletMap[t.outlet_id]?.name?.toLowerCase().includes(s)
    );
  }, [tickets, q, outletMap]);

  // Group tickets by outlet — but only when we're showing a mixed set. If the
  // user already zoomed in on a single outlet or on Internal Jobs, keep the
  // flat list so we don't render a header of one.
  const shouldGroup = !outletId && typeFilter !== "internal_job";
  const groups = useMemo(() => {
    if (!list || !shouldGroup) return null;
    const buckets = new Map();
    for (const t of list) {
      const key = t.outlet_id || "__none__";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(t);
    }
    // Sort: outlet code alpha, "Unknown" last
    const arr = Array.from(buckets.entries()).map(([oid, ts]) => ({
      outlet: outletMap[oid],
      outletId: oid,
      tickets: ts,
    }));
    arr.sort((a, b) => {
      const ac = a.outlet?.code || "zzz";
      const bc = b.outlet?.code || "zzz";
      return ac.localeCompare(bc);
    });
    return arr;
  }, [list, shouldGroup, outletMap]);

  const setF = (k) => {
    setFilter(k);
    const np = new URLSearchParams(params);
    if (k === "all") np.delete("filter"); else np.set("filter", k);
    setParams(np, { replace: true });
  };

  const setOutlet = (id) => {
    setOutletId(id);
    const np = new URLSearchParams(params);
    if (!id) np.delete("outlet_id"); else np.set("outlet_id", id);
    setParams(np, { replace: true });
  };

  const toggleGroup = (oid) => setCollapsed((s) => ({ ...s, [oid]: !s[oid] }));

  const Row = ({ t }) => (
    <button
      key={t.id}
      data-testid={`ticket-row-${t.id}`}
      onClick={() => nav(`/tickets/${t.id}`)}
      className={`w-full text-left rounded-xl border bg-white p-4 hover:border-indigo-300 card-lift fade-up ${t.is_overdue ? "overdue-tint" : "border-slate-200"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <TypePill type={t.type} />
            <PriorityPill priority={t.priority} />
            {!shouldGroup && outletMap[t.outlet_id] && (
              <span className="text-[11px] font-medium text-slate-500">
                {outletMap[t.outlet_id].code} · {outletMap[t.outlet_id].name}
              </span>
            )}
          </div>
          <div className="font-semibold text-slate-900 truncate">{t.title}</div>
          <div className="text-xs text-slate-500 mt-1 line-clamp-1">{t.description}</div>
          <div className="text-[11px] text-slate-500 mt-2">
            {relTime(t.created_at)} • {t.created_by_name}
            {t.reopened_count > 0 && <span className="ml-2 text-rose-600 font-medium">Reopened ×{t.reopened_count}</span>}
          </div>
        </div>
        <StatusPill status={t.status} overdue={t.is_overdue} className="flex-shrink-0" />
      </div>
    </button>
  );

  const visibleOutlets = outlets.filter((o) => !o.is_internal);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-900">
            {typeFilter === "internal_job" ? "Internal Jobs" : "Tickets"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {typeFilter === "internal_job"
              ? "Back-office tasks only — outlet tickets are hidden here."
              : outletId && outletMap[outletId] ? `Filtered by outlet: ${outletMap[outletId].name}` : "Grouped by outlet"}
          </p>
        </div>
        {user?.role !== "manager" && user?.role !== "op_manager" && (
          <button data-testid="tickets-new-btn" onClick={() => nav("/tickets/new")}
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm">
            <Plus className="w-4 h-4" /> New Ticket
          </button>
        )}
      </div>

      {/* Filter chips + outlet picker + search */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(f => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setF(f.key)}
              className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
                filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          {typeFilter !== "internal_job" && visibleOutlets.length > 1 && (
            <Select value={outletId || "__all__"} onValueChange={(v) => setOutlet(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="tickets-outlet-filter" className="h-10 w-full sm:w-56">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <SelectValue placeholder="All outlets" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All outlets</SelectItem>
                {visibleOutlets.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="tickets-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, description, outlet…" className="pl-9 h-10" />
          </div>
        </div>
      </div>

      {/* List */}
      {list === null ? (
        <div className="space-y-2">
          {[0,1,2,3].map(i => <div key={i} className="h-20 rounded-xl bg-white border border-slate-200 animate-pulse" />)}
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <TicketIcon className="w-8 h-8 text-slate-400 mx-auto" />
          <div className="mt-3 font-semibold text-slate-800">No tickets found</div>
          <div className="text-sm text-slate-500">Try a different filter or clear your search.</div>
        </div>
      ) : shouldGroup && groups ? (
        <div className="space-y-6">
          {groups.map(({ outlet, outletId: oid, tickets: ts }) => {
            const open = !collapsed[oid];
            const overdue = ts.filter((t) => t.is_overdue).length;
            const openN = ts.filter((t) => t.status !== "resolved").length;
            return (
              <section key={oid} data-testid={`outlet-group-${outlet?.code || oid}`}>
                <button
                  type="button"
                  data-testid={`outlet-group-toggle-${outlet?.code || oid}`}
                  onClick={() => toggleGroup(oid)}
                  className="w-full flex items-center gap-2 py-2 mb-2 text-left"
                >
                  {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <Building2 className="w-4 h-4 text-indigo-600" />
                  <span className="font-display font-bold text-slate-900">
                    {outlet ? `${outlet.code} · ${outlet.name}` : "Unassigned outlet"}
                  </span>
                  <span className="text-xs text-slate-500">· {ts.length} ticket{ts.length === 1 ? "" : "s"}</span>
                  {openN > 0 && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                      {openN} open
                    </span>
                  )}
                  {overdue > 0 && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-rose-100 text-rose-700 px-2 py-0.5">
                      {overdue} overdue
                    </span>
                  )}
                </button>
                {open && (
                  <div className="space-y-2">
                    {ts.map((t) => <Row key={t.id} t={t} />)}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((t) => <Row key={t.id} t={t} />)}
        </div>
      )}
    </div>
  );
}
